import { useEffect, useState } from 'react';
import { workspacesApi } from '../lib/api';

interface Props {
  /** The workspace id — required so we can call our Browser-Rendering proxy. */
  workspaceId: string;
  /** The workspace's website URL. When null/empty, only the placeholder renders. */
  websiteUrl: string | null | undefined;
  /** Accessible label — falls back to the domain/name; mandatory from callers. */
  alt: string;
  /** Tailwind size classes. Default matches the Workspaces list card. */
  className?: string;
}

/**
 * Small website thumbnail for workspace cards. Calls the
 * `GET /api/workspaces/:id/screenshot` endpoint, which is backed by
 * Cloudflare Browser Rendering + R2 (ADR-023). The first render per
 * (workspace × URL × week) can take a few seconds — while the request is
 * in flight, we show a neutral placeholder. Cached hits return instantly.
 */
export function WorkspaceThumbnail({
  workspaceId,
  websiteUrl,
  alt,
  className = 'w-24 h-18',
}: Props) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!websiteUrl) {
      setSrc(null);
      setFailed(false);
      return;
    }

    let cancelled = false;
    setFailed(false);
    setSrc(null);

    workspacesApi
      .screenshot(workspaceId)
      .then((res) => {
        if (cancelled) return;
        if (res?.success && res.data?.url) {
          setSrc(res.data.url);
        } else {
          setFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceId, websiteUrl]);

  const showImage = src && !failed;

  return (
    <div
      className={`${className} flex-shrink-0 rounded-lg overflow-hidden bg-navy-800 border border-navy-700 flex items-center justify-center`}
      aria-hidden={!showImage ? true : undefined}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onError={() => setFailed(true)}
          className="w-full h-full object-cover"
        />
      ) : (
        <svg
          className="w-8 h-8 text-slate-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 7.5A2.5 2.5 0 015.5 5h13A2.5 2.5 0 0121 7.5v9a2.5 2.5 0 01-2.5 2.5h-13A2.5 2.5 0 013 16.5v-9zM3 15l4.5-4.5a1.5 1.5 0 012.12 0L14 15m3-3l1.5-1.5a1.5 1.5 0 012.12 0L21 12m-6-4a1 1 0 100-2 1 1 0 000 2z"
          />
        </svg>
      )}
    </div>
  );
}
